import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ApiError, api } from '../lib/api';
import { useApp } from '../lib/app-context';
import { KYC_LABEL } from '../lib/format';
import { cityOptions, useCities } from '../lib/places';
import { Badge, Field, Notice, Select, Sheet, Spinner, TextInput, Toggle } from '../components/ui';
import PushPrompt from '../components/PushPrompt';
import type { SessionUser } from '../lib/types';
import { t } from '../lib/i18n';

/** Profile, notification preferences, password, sessions, account deletion. */

type MeResponse = {
  user: SessionUser;
  bio: string | null;
  settings: {
    notifySms: boolean;
    notifyEmail: boolean;
    notifyPush: boolean;
    notifyWhatsapp: boolean;
    timezone: string;
    searchPrefs: { budgetMaxCentimes?: number; city?: string };
  };
};

export default function Settings() {
  const { user, refreshUser, signOut, theme, toggleTheme, toast, reportError } = useApp();
  const navigate = useNavigate();
  const [data, setData] = useState<MeResponse | null>(null);
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [bio, setBio] = useState('');
  const [city, setCity] = useState('');
  const [universityName, setUniversityName] = useState('');
  const [locale, setLocale] = useState('fr');
  const [phone, setPhone] = useState('');
  /** Set after an email change: the new address has to be proven before it counts. */
  const [pendingEmail, setPendingEmail] = useState<string | null>(null);
  const [emailCode, setEmailCode] = useState('');
  const [emailDevCode, setEmailDevCode] = useState<string | null>(null);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [deleteOpen, setDeleteOpen] = useState(false);
  const cities = useCities();

  const load = async () => {
    try {
      const response = await api.get<MeResponse>('/users/me');
      setData(response);
      setFullName(response.user.fullName);
      setEmail(response.user.email ?? '');
      setBio(response.bio ?? '');
      setCity(response.user.city ?? '');
      setUniversityName(response.user.universityName ?? '');
      setLocale(response.user.locale);
      setPhone(response.user.phone ?? '');
    } catch (error) {
      reportError(error);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveProfile = async () => {
    setBusy(true);
    setErrors({});
    try {
      const response = await api.patch<{ verificationSent?: boolean; devCode?: string }>(
        '/users/me',
        {
          fullName,
          ...(email ? { email } : {}),
          // An emptied field clears the number rather than being ignored.
          phone: phone.trim() ? phone.trim() : null,
          bio,
          ...(city ? { city } : {}),
          ...(universityName ? { universityName } : {}),
          locale,
        },
      );
      await refreshUser();
      await load();
      if (response.verificationSent) {
        setPendingEmail(email.trim().toLowerCase());
        setEmailDevCode(response.devCode ?? null);
        toast(t("Profil enregistré. Un code a été envoyé à votre nouvelle adresse."), 'ok');
      } else {
        toast(t("Profil enregistré."), 'ok');
      }
    } catch (error) {
      const apiError = reportError(error);
      if (apiError instanceof ApiError) setErrors(apiError.fieldErrors());
    } finally {
      setBusy(false);
    }
  };

  const confirmNewEmail = async () => {
    if (!pendingEmail) return;
    setBusy(true);
    try {
      await api.post('/auth/otp/verify', { email: pendingEmail, code: emailCode });
      setPendingEmail(null);
      setEmailCode('');
      setEmailDevCode(null);
      await refreshUser();
      toast(t("Nouvelle adresse vérifiée."), 'ok');
    } catch (error) {
      reportError(error);
    } finally {
      setBusy(false);
    }
  };

  const saveNotification = async (key: string, value: boolean) => {
    setData((current) =>
      current ? { ...current, settings: { ...current.settings, [key]: value } } : current,
    );
    try {
      await api.patch('/users/me/settings', { [key]: value });
    } catch (error) {
      reportError(error);
      await load();
    }
  };

  const changePassword = async () => {
    setBusy(true);
    setErrors({});
    try {
      await api.post('/auth/password/change', { currentPassword, newPassword });
      toast(t("Mot de passe modifié. Reconnectez-vous."), 'ok');
      await signOut();
      navigate('/connexion');
    } catch (error) {
      const apiError = reportError(error);
      if (apiError instanceof ApiError) setErrors(apiError.fieldErrors());
    } finally {
      setBusy(false);
    }
  };

  if (!data) {
    return (
      <div className="page center" style={{ paddingTop: 60 }}>
        <Spinner label={t("Chargement…")} />
      </div>
    );
  }

  return (
    <div className="page page--narrow stack stack--loose">
      <h1 style={{ fontSize: 26 }}>{t("Paramètres")}</h1>

      <section className="card card--pad stack">
        <h2>{t("Profil")}</h2>
        <Field label={t("Nom complet")} error={errors.fullName}>
          <TextInput value={fullName} onChange={setFullName} />
        </Field>
        <Field
          label={t("Email")}
          hint={t("Changer d’adresse demande de vérifier la nouvelle avec un code.")}
          error={errors.email}
        >
          <TextInput value={email} onChange={setEmail} type="email" inputMode="email" autoComplete="email" />
        </Field>
        {pendingEmail ? (
          <div className="card card--pad stack stack--tight" style={{ background: 'var(--surface-2)' }}>
            <p className="small" style={{ margin: 0 }}>
              {t("Code envoyé à")} <strong>{pendingEmail}</strong>{t(". Tant qu’il n’est pas saisi, vous ne pouvez ni réserver ni publier.")}
            </p>
            {emailDevCode ? (
              <Notice tone="warn">
                {t("Mode développement : le code est")} <strong>{emailDevCode}</strong>.
              </Notice>
            ) : null}
            <div className="row" style={{ gap: 8 }}>
              <div style={{ flex: 1 }}>
                <TextInput
                  value={emailCode}
                  onChange={(value) => setEmailCode(value.replace(/\D/g, '').slice(0, 6))}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder="000000"
                  style={{ letterSpacing: '0.4em', textAlign: 'center' }}
                />
              </div>
              <button
                className="btn btn--sm"
                disabled={busy || emailCode.length !== 6}
                onClick={confirmNewEmail}
              >
                {t("Vérifier")}
              </button>
            </div>
          </div>
        ) : null}
        <Field
          label={t("Téléphone (optionnel)")}
          hint={t("Pour que l’hôte ou le locataire puisse vous joindre. Jamais affiché publiquement.")}
          error={errors.phone}
        >
          <TextInput value={phone} onChange={setPhone} type="tel" inputMode="tel" autoComplete="tel" placeholder="06 12 34 56 78" />
        </Field>
        <div className="row row--wrap" style={{ gap: 10 }}>
          <div style={{ flex: '1 1 150px' }}>
            <Field label={t("Ville")}>
              <Select
                value={city}
                onChange={setCity}
                options={cityOptions(cities, t("Non précisée"))}
              />
            </Field>
          </div>
          <div style={{ flex: '1 1 150px' }}>
            <Field label={t("Université")}>
              <TextInput value={universityName} onChange={setUniversityName} placeholder="UM5" />
            </Field>
          </div>
        </div>
        <Field label={t("Langue")}>
          <Select
            value={locale}
            onChange={setLocale}
            options={[
              { value: 'fr', label: t("Français") },
              { value: 'ar', label: t("العربية") },
              { value: 'en', label: t("English") },
            ]}
          />
        </Field>
        <Field label={t("Bio")}>
          <textarea
            className="textarea"
            rows={3}
            value={bio}
            onChange={(event) => setBio(event.target.value)}
            placeholder={t("Deux lignes pour vous présenter aux hôtes.")}
          />
        </Field>
        <button className="btn" disabled={busy} onClick={saveProfile}>
          {busy ? t("Enregistrement…") : t("Enregistrer")}
        </button>
      </section>

      <section className="card card--pad stack">
        <h2>{t("Vérification")}</h2>
        <div className="row row--between row--wrap" style={{ gap: 10 }}>
          <div className="row row--wrap" style={{ gap: 8 }}>
            <Badge tone={user?.kycStatus === 'APPROVED' ? 'badge--verified' : 'badge--warn'}>
              {t("Identité :")} {KYC_LABEL[user?.kycStatus ?? 'NONE']}
            </Badge>
            <Badge tone={user?.emailVerified ? 'badge--ok' : 'badge--warn'}>
              {t("Email :")} {user?.emailVerified ? t("vérifié") : t("non vérifié")}
            </Badge>
          </div>
          <Link className="btn btn--ghost btn--sm" to="/verification">
            {t("Gérer")}
          </Link>
        </div>
      </section>

      <section className="card card--pad stack">
        <h2>{t("Notifications")}</h2>
        {/* No SMS toggle: Bedly sends no SMS, and a switch that does nothing
            is worse than no switch. The column stays for when it does. */}
        <Toggle
          on={data.settings.notifyWhatsapp}
          onChange={(value) => void saveNotification('notifyWhatsapp', value)}
          label={t("WhatsApp")}
        />
        <Toggle
          on={data.settings.notifyEmail}
          onChange={(value) => void saveNotification('notifyEmail', value)}
          label={t("Email")}
        />
        <Toggle
          on={data.settings.notifyPush}
          onChange={(value) => void saveNotification('notifyPush', value)}
          label={t('Notifications push (tous vos appareils)')}
        />
        <PushPrompt variant="settings" />
      </section>

      <section className="card card--pad stack">
        <h2>{t("Apparence")}</h2>
        <Toggle on={theme === 'dark'} onChange={toggleTheme} label={t("Thème sombre")} />
      </section>

      <section className="card card--pad stack">
        <h2>{t("Sécurité")}</h2>
        {user && !user.hasPassword ? (
          <Notice tone="info">
            {t("Vous vous connectez avec Google. Pour ajouter aussi un mot de passe, utilisez « Mot de passe oublié » sur l’écran de connexion : le code arrivera à votre adresse Gmail.")}
          </Notice>
        ) : null}
        <Field label={t("Mot de passe actuel")} error={errors.currentPassword}>
          <TextInput value={currentPassword} onChange={setCurrentPassword} type="password" />
        </Field>
        <Field
          label={t("Nouveau mot de passe")}
          error={errors.newPassword}
          hint={t("8 caractères minimum, lettres et chiffres.")}
        >
          <TextInput value={newPassword} onChange={setNewPassword} type="password" />
        </Field>
        <button
          className="btn btn--ghost"
          disabled={busy || !currentPassword || !newPassword || !user?.hasPassword}
          onClick={changePassword}
        >
          {t("Changer le mot de passe")}
        </button>
        <button
          className="btn btn--quiet"
          disabled={busy}
          onClick={async () => {
            await api.post('/auth/logout-all');
            toast(t("Déconnecté de tous les appareils."));
            await signOut();
            navigate('/');
          }}
        >
          {t("Se déconnecter partout")}
        </button>
      </section>

      <section className="card card--pad stack">
        <h2>{t("Compte")}</h2>
        <button
          className="btn btn--ghost"
          onClick={async () => {
            await signOut();
            navigate('/');
          }}
        >
          {t("Se déconnecter")}
        </button>
        <button className="btn btn--danger" onClick={() => setDeleteOpen(true)}>
          {t("Supprimer mon compte")}
        </button>
      </section>

      {deleteOpen ? (
        <Sheet title={t("Supprimer votre compte ?")} onClose={() => setDeleteOpen(false)}>
          <div className="stack">
            <Notice tone="danger">
              {t("Votre profil est anonymisé et vos annonces retirées. Les réservations et avis dont d’autres personnes dépendent sont conservés sans votre nom. Cette action est irréversible.")}
            </Notice>
            <button
              className="btn btn--danger btn--block"
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                try {
                  await api.delete('/users/me');
                  await signOut();
                  toast(t("Compte supprimé."));
                  navigate('/');
                } catch (error) {
                  reportError(error);
                } finally {
                  setBusy(false);
                }
              }}
            >
              {t("Supprimer définitivement")}
            </button>
            <button className="btn btn--quiet btn--block" onClick={() => setDeleteOpen(false)}>
              {t("Annuler")}
            </button>
          </div>
        </Sheet>
      ) : null}
    </div>
  );
}
